import React, { useEffect, useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { ArrowRight, Check, Eye, EyeOff, Lock, Mail, ShieldCheck, UserRound } from "lucide-react";

export default function Login() {
  const { user, loading, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const savedSetting = localStorage.getItem("azmiey_remember_me");
    if (savedSetting === "true") setRememberMe(true);
  }, []);

  if (loading) return null;
  if (user && user !== false) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      localStorage.setItem("azmiey_remember_me", rememberMe ? "true" : "false");
      toast.success("Berjaya log masuk");
      nav(res.user?.role === "admin" ? "/admin" : "/dashboard", { replace: true });
    } else {
      toast.error(res.error || "Login gagal");
    }
  };

  const onGoogleClick = () => {
    toast.info("Google login belum tersedia buat masa ini.");
  };

  return (
    <div className="min-h-screen bg-[#0B0B0D] text-zinc-900">
      <header className="relative overflow-hidden border-b border-white/10 bg-[#29292D]">
        <div className="absolute -left-10 top-0 h-44 w-44 rounded-full bg-violet-500/15 blur-3xl" />
        <div className="absolute right-0 top-[-2rem] h-52 w-52 rounded-full bg-violet-600/20 blur-3xl" />
        <div className="relative mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-baseline gap-1">
            <span className="font-heading text-[1.65rem] font-black tracking-[-0.06em] text-white sm:text-[2rem]">AZMIEY</span>
            <span className="font-heading text-[1.65rem] font-black tracking-[-0.06em] text-violet-500 sm:text-[2rem]">STORE</span>
          </Link>
          <p className="mt-3 text-[0.68rem] font-medium uppercase tracking-[0.32em] text-violet-300 sm:text-[0.72rem]">
            Login ke akaun anda
          </p>
        </div>
      </header>

      <main className="mx-auto flex max-w-5xl items-center justify-center px-4 py-8 sm:px-6 lg:py-12">
        <div className="w-full max-w-[500px]">
          <div className="rounded-[24px] border border-violet-100/80 bg-white p-5 shadow-[0_30px_90px_rgba(16,10,26,0.14)] sm:p-8">
            <div className="mb-6 flex justify-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-violet-100 text-violet-600 shadow-[0_18px_35px_rgba(124,58,237,0.18)]">
                <UserRound className="h-8 w-8" />
              </div>
            </div>

            <div className="text-center">
              <h1 className="font-heading text-[2rem] font-black leading-[1.04] tracking-[-0.05em] text-zinc-900 sm:text-[2.5rem]">
                Selamat Datang
                <span className="mt-1 block text-violet-600">Kembali!</span>
              </h1>
              <p className="mt-3 text-sm leading-6 text-zinc-600 sm:text-[15px]">
                Log masuk ke akaun anda untuk meneruskan pembelian.
              </p>
            </div>

            <form onSubmit={onSubmit} data-testid="login-form" className="mt-7 space-y-5">
              <div>
                <label className="mb-2 block text-sm font-medium text-zinc-700">Email atau No. Telefon</label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                  <Input
                    id="login-email-input"
                    data-testid="login-email-input"
                    type="text"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="Email atau No. Telefon"
                    className="h-[52px] rounded-2xl border border-zinc-200 bg-zinc-50 pl-11 pr-4 text-[15px] text-zinc-900 placeholder:text-zinc-400 focus-visible:border-violet-300 focus-visible:ring-violet-300"
                  />
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-zinc-700">Kata Laluan</label>
                <div className="relative">
                  <Lock className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                  <Input
                    id="login-password-input"
                    data-testid="login-password-input"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="Kata Laluan"
                    className="h-[52px] rounded-2xl border border-zinc-200 bg-zinc-50 pl-11 pr-12 text-[15px] text-zinc-900 placeholder:text-zinc-400 focus-visible:border-violet-300 focus-visible:ring-violet-300"
                  />
                  <button
                    type="button"
                    aria-label={showPassword ? "Sembunyikan kata laluan" : "Tunjukkan kata laluan"}
                    onClick={() => setShowPassword((value) => !value)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-zinc-500 transition hover:text-violet-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3 pt-1">
                <label className="flex cursor-pointer items-center gap-2 text-sm text-zinc-600">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-4 w-4 rounded border-zinc-300 text-violet-600 focus:ring-violet-500"
                  />
                  <span>Ingat saya</span>
                </label>

                <button
                  type="button"
                  onClick={() => toast.info("Sambungan reset kata laluan akan datang." )}
                  className="text-sm font-semibold text-violet-600 transition hover:text-violet-700"
                  data-testid="login-forgot-password-link"
                >
                  Lupa kata laluan?
                </button>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="login-submit-button"
                id="login-submit"
                className="inline-flex h-[54px] w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-violet-600 to-violet-500 text-base font-semibold text-white shadow-[0_18px_35px_rgba(124,58,237,0.35)] transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_20px_40px_rgba(124,58,237,0.4)] disabled:cursor-not-allowed disabled:opacity-70"
              >
                {submitting ? "Memuat..." : (
                  <>
                    <ShieldCheck className="h-5 w-5" />
                    Log Masuk
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            </form>

            <div className="my-6 flex items-center gap-3 text-[0.7rem] font-semibold uppercase tracking-[0.28em] text-zinc-400">
              <span className="h-px flex-1 bg-zinc-200" />
              atau
              <span className="h-px flex-1 bg-zinc-200" />
            </div>

            <button
              type="button"
              onClick={onGoogleClick}
              className="inline-flex h-[52px] w-full items-center justify-center gap-3 rounded-2xl border border-violet-200 bg-white text-[15px] font-semibold text-zinc-700 shadow-sm transition hover:border-violet-300 hover:bg-violet-50"
            >
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-violet-50 text-xs font-black text-violet-600">G</span>
              Log Masuk dengan Google
            </button>

            <p className="mt-7 text-center text-sm text-zinc-600">
              Tiada akaun?{" "}
              <Link to="/register" data-testid="link-to-register" className="font-semibold text-violet-600 transition hover:text-violet-700">
                Daftar sekarang
              </Link>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
